import React, { useCallback, useRef } from 'react';
import { Grid, Typography } from '@material-ui/core';
import * as yup from 'yup';
import FileInput from '../../../../../Components/Inputs/FileInput';
import FileGridList from '../../../../../Components/Inputs/File/FileGridList';
import { VALID_FILE_EXTENSIONS } from '../../../Pqr_enums';
import { convertBytesToMegaBytes } from '../../../../../Utils/Calculations/file';
import BaseTooltip from '../../../../../Components/Tooltip/BaseTooltip';
import { maxMBSizePqrFiles } from '../../../../../Configs/general';

const getFileSchema = validFileExtensions =>
  yup.object({
    size: yup
      .number()
      // eslint-disable-next-line no-template-curly-in-string
      .test('fileSize', '${path}->Tamaño máximo: 10 MB', value => {
        return value <= 10240000;
      }),
    name: yup.string(),
    type: yup.string(),
    attachemntType: yup.string(),
    extension: yup
      .string()
      // eslint-disable-next-line no-template-curly-in-string
      .test('extension', '${path}->Archivo no permitido', extension =>
        validFileExtensions.includes(extension)
      ),
    file: yup.mixed()
  });

const parseFile = (file, validFileExtensions = VALID_FILE_EXTENSIONS) => {
  const { name, size, type } = file;

  const extension = name
    .split('.')
    .pop()
    .toLowerCase();

  const preparedFile = {
    name,
    size,
    type,
    extension,
    file,
    error: false
  };

  try {
    const fileSchema = getFileSchema(validFileExtensions);

    fileSchema.validateSync(preparedFile, {
      abortEarly: false
    });
  } catch (err) {
    for (const errorMessage of err.errors) {
      const message = errorMessage.split('->')[1];

      preparedFile.error = true;
      preparedFile.errorMessage = message;
    }
  }

  return preparedFile;
};

const AttachmentInput = ({
  classes,
  index = 0,
  label,
  helperText,
  required,
  validFileExtensions = VALID_FILE_EXTENSIONS,
  templateLink = null,
  fileInputId,
  files,
  totalSize,
  setFiles,
  setTotalSize
}) => {
  const fileInputRef = useRef(null);

  const handleChangeFile = useCallback(
    e => {
      const inputFile = Array.from(e.target.files)[0];

      const parsedFile = parseFile(inputFile, validFileExtensions);

      const updatedFiles = files.slice();
      updatedFiles[index] = parsedFile;

      const totalFilesSize = updatedFiles
        .filter(file => file)
        .reduce((total, file) => total + file.size, 0);

      setFiles(updatedFiles);
      setTotalSize(convertBytesToMegaBytes(totalFilesSize));
    },
    [files, validFileExtensions, setFiles, setTotalSize, index]
  );

  const handleDeleteFile = useCallback(() => {
    const updatedFiles = files.map((file, i) => {
      if (i === index) {
        return null;
      }
      return file;
    });

    const totalFilesSize = updatedFiles
      .filter(file => file)
      .reduce((total, file) => total + file.size, 0);

    setFiles(updatedFiles);
    setTotalSize(convertBytesToMegaBytes(totalFilesSize));
    fileInputRef.current.value = '';
  }, [files, index, setFiles, setTotalSize, fileInputRef]);

  const renderLabel = () => (
    <Typography className={classes.subtitle}>
      {label}{' '}
      {templateLink && (
        <a href={templateLink} download>
          (Descargar plantilla)
        </a>
      )}
    </Typography>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {helperText && (
          <BaseTooltip
            tooltipText={helperText}
            text={renderLabel()}
            textClass={classes.subtitle}
          />
        )}
        {!helperText && renderLabel()}
      </Grid>

      <Grid item xs="auto">
        <FileInput
          id={`${fileInputId}_${index}`}
          ref={fileInputRef}
          name={`attachments.${index}`}
          accept={validFileExtensions.map(ext => `.${ext}`).join(',')}
          text="Adjuntar archivo"
          helperText={
            files[index] === null && required && 'Este documento es requerido'
          }
          inputProps={{
            onChange: handleChangeFile
          }}
          error={
            (files[index] && files[index].error) ||
            totalSize > maxMBSizePqrFiles ||
            (required && files[index] === null)
          }
          disabled={totalSize > maxMBSizePqrFiles}
          buttonProps={{
            color: 'default'
          }}
          iconVisible
        />
      </Grid>
      {files && files[index] && (
        <FileGridList
          id={`Pqr_detail_filelist_${index}`}
          files={files.slice(index, index + 1)}
          onDeleteFile={handleDeleteFile}
          className={classes.filelistItem}
          xs={9}
        />
      )}
    </Grid>
  );
};

export default AttachmentInput;
